import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { User, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { sendWelcomeEmail } from '../lib/email';
import { logger } from '../lib/logger';
import { withTimeout } from '../lib/async';
import type { Profile, AuthState, LoginCredentials, RegisterCredentials, UserRole } from '../types';

interface AuthContextType extends AuthState {
  signIn: (credentials: LoginCredentials) => Promise<{ error: AuthError | null }>;
  signUp: (credentials: RegisterCredentials) => Promise<{ error: AuthError | null; alreadyExists?: boolean }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
  updateProfile: (data: Partial<Profile>) => Promise<{ error: string | null }>;
  deleteAccount: () => Promise<{ error: string | null }>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Cache simple pour éviter les requêtes multiples
const profileCache: { [key: string]: { profile: Profile; timestamp: number } } = {};
const CACHE_DURATION = 10000; // 10 secondes
// Flag pour éviter les requêtes concurrentes
let fetchingProfile: Record<string, Promise<Profile | null> | undefined> = {};

function clearProfileCache(userId?: string): void {
  if (userId) {
    delete profileCache[userId];
    delete fetchingProfile[userId];
  } else {
    for (const key of Object.keys(profileCache)) {
      delete profileCache[key];
    }
    for (const key of Object.keys(fetchingProfile)) {
      delete fetchingProfile[key];
    }
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [supabaseUser, setSupabaseUser] = useState<User | null>(null);
  const hasInitialSessionRef = useRef(false);
  const lastAuthUserIdRef = useRef<string | null>(null);
  const inFlightAuthRef = useRef<Promise<void> | null>(null);

  const isAbortError = (error: unknown): boolean => {
    if (!error) return false;
    if (error instanceof Error && error.name === 'AbortError') return true;
    if (typeof error === 'object' && 'message' in error) {
      const message = String((error as { message?: string }).message || '');
      return message.includes('AbortError');
    }
    return false;
  };

  const buildFallbackProfile = (authUser: User, roleOverride?: UserRole): Profile => ({
    id: authUser.id,
    email: authUser.email || '',
    full_name:
      (authUser.user_metadata?.full_name as string | undefined) ||
      authUser.email ||
      null,
    phone: null,
    role: roleOverride || 'customer',
    avatar_url: null,
    created_at: authUser.created_at || new Date().toISOString(),
    updated_at: new Date().toISOString(),
  });

  const fetchProfile = useCallback(async (
    userId: string,
    options?: { skipCache?: boolean; retries?: number; authUser?: User }
  ): Promise<Profile | null> => {
    const { skipCache = false, retries = 2 } = options || {};

    // Vérifier le cache d'abord (sauf si skipCache)
    if (!skipCache) {
      const cached = profileCache[userId];
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        logger.log('fetchProfile: Utilisation du cache pour', userId);
        return cached.profile;
      }
    } else {
      delete profileCache[userId];
    }

    // Si une requête est déjà en cours (et pas de skipCache), attendre son résultat
    if (!skipCache && fetchingProfile[userId]) {
      logger.log('fetchProfile: Requête déjà en cours, attente...');
      return fetchingProfile[userId];
    }

    const fetchPromise = (async (): Promise<Profile | null> => {
      let lastError: { code?: string; message?: string } | null = null;

      for (let attempt = 0; attempt <= retries; attempt++) {
        try {
          if (attempt > 0) {
            await new Promise(resolve => setTimeout(resolve, 200 * attempt));
            logger.log(`fetchProfile: Retry ${attempt} pour`, userId);
          }

          logger.log('fetchProfile: Requête Supabase pour', userId);
          const queryFn = async () => {
            return await supabase
              .from('profiles')
              .select('*')
              .eq('id', userId)
              .maybeSingle();
          };
          const result = await withTimeout(queryFn(), 5000, 'Timeout chargement profil') as {
            data: Profile | null;
            error: { code?: string; message?: string } | null;
          };
          const { data, error } = result;

          if (error) {
            lastError = error;
            logger.warn(`fetchProfile: Erreur attempt ${attempt}:`, error.code, error.message);
            continue;
          }

          if (data) {
            profileCache[userId] = { profile: data, timestamp: Date.now() };
            logger.log('fetchProfile: SUCCESS! role =', data.role);
            return data;
          }

          logger.warn('fetchProfile: Aucun profil trouve pour', userId);
          return null;
        } catch (err) {
          if (isAbortError(err)) {
            logger.warn('fetchProfile: AbortError attempt', attempt);
            lastError = { message: 'AbortError' };
            continue;
          }
          logger.error(`fetchProfile: Exception attempt ${attempt}:`, err);
          lastError = { message: err instanceof Error ? err.message : 'Unknown error' };
          continue;
        }
      }

      logger.error('fetchProfile: Tous les retries épuisés pour', userId, lastError);
      return null;
    })();

    fetchingProfile[userId] = fetchPromise;
    try {
      return await fetchPromise;
    } finally {
      delete fetchingProfile[userId];
    }
  }, []);

  const refreshUser = useCallback(async () => {
    if (!supabaseUser) return;
    const profile = await fetchProfile(supabaseUser.id, { skipCache: true });
    setUser(profile);
  }, [supabaseUser, fetchProfile]);

  useEffect(() => {
    let isMounted = true;

    const handleAuthEvent = async (
      event: string,
      session: { user: User } | null
    ) => {
      if (!isMounted) return;
      logger.log('Auth event:', event, session?.user?.email);

      if (
        event === 'SIGNED_IN' &&
        hasInitialSessionRef.current &&
        session?.user?.id &&
        lastAuthUserIdRef.current === session.user.id
      ) {
        logger.log('SIGNED_IN: Ignoré (INITIAL_SESSION déjà traité)');
        return;
      }

      try {
        const run = async () => {
          if (
            (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'USER_UPDATED') &&
            session?.user
          ) {
            lastAuthUserIdRef.current = session.user.id;
            logger.log(`${event}: Setting supabaseUser...`);
            setSupabaseUser(session.user);

            const cached = profileCache[session.user.id];
            const cachedProfile =
              cached && Date.now() - cached.timestamp < CACHE_DURATION ? cached.profile : null;
            const baseProfile = cachedProfile || buildFallbackProfile(session.user);
            if (isMounted) {
              setUser(baseProfile);
            }

            logger.log(`${event}: Fetching profile for`, session.user.id);
            void (async () => {
              const profile = await fetchProfile(session.user.id, { authUser: session.user });
              if (profile && isMounted) {
                logger.log(`${event}: Profile fetched:`, profile);
                setUser(profile);
              }
            })();
          } else if (event === 'INITIAL_SESSION' && !session) {
            logger.log('INITIAL_SESSION: No session found');
          } else if (event === 'SIGNED_OUT') {
            clearProfileCache();
            setSupabaseUser(null);
            setUser(null);
          } else if (event === 'TOKEN_REFRESHED' && session?.user) {
            lastAuthUserIdRef.current = session.user.id;
            logger.log('TOKEN_REFRESHED: Updating supabaseUser and refreshing profile');
            setSupabaseUser(session.user);
            // Forcer le re-fetch du profil (skip cache) pour avoir des données fraîches
            void (async () => {
              const profile = await fetchProfile(session.user.id, {
                skipCache: true,
                authUser: session.user,
              });
              if (profile && isMounted) {
                setUser(profile);
              }
            })();
          }
        };

        if (!inFlightAuthRef.current) {
          inFlightAuthRef.current = run();
        }
        await inFlightAuthRef.current;
      } catch (error) {
        if (!isAbortError(error) && isMounted) {
          logger.error('Erreur dans onAuthStateChange:', error);
        }
      } finally {
        inFlightAuthRef.current = null;
        // isLoading passe à false uniquement après traitement de INITIAL_SESSION
        if (event === 'INITIAL_SESSION' && isMounted) {
          hasInitialSessionRef.current = true;
          setIsLoading(false);
        }
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(handleAuthEvent);

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const signIn = async (credentials: LoginCredentials) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (!error) {
        const authUser = data?.user;
        if (authUser) {
          setSupabaseUser(authUser);
          setUser(buildFallbackProfile(authUser));
        }
      }

      return { error };
    } catch (error) {
      if (isAbortError(error)) {
        return { error: null };
      }
      throw error;
    }
  };

  const signUp = async (credentials: RegisterCredentials) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email: credentials.email,
        password: credentials.password,
        options: {
          data: {
            full_name: credentials.full_name,
          },
          emailRedirectTo: `${window.location.origin}/email-confirme`,
        },
      });

      // Supabase ne retourne pas d'erreur si l'email existe déjà (anti-énumération)
      // On détecte ce cas via identities vide
      if (!error && data?.user && (!data.user.identities || data.user.identities.length === 0)) {
        return { error: null, alreadyExists: true };
      }

      // Envoyer l'email de bienvenue si inscription réussie (ne pas bloquer si échec)
      if (!error && !(!data?.user?.identities || data.user.identities.length === 0)) {
        sendWelcomeEmail({
          customerName: credentials.full_name || credentials.email.split('@')[0],
          customerEmail: credentials.email,
        }).catch((err) => logger.error('Failed to send welcome email:', err));
      }

      return { error };
    } catch (error) {
      if (isAbortError(error)) {
        return { error: null };
      }
      throw error;
    }
  };

  const signOut = async () => {
    try {
      // Nettoyer le cache et l'état local immédiatement
      clearProfileCache();
      setUser(null);
      setSupabaseUser(null);

      // Tenter la déconnexion côté serveur
      const { error } = await supabase.auth.signOut();

      if (error) {
        logger.error('Erreur lors de la déconnexion:', error);
        // Même en cas d'erreur serveur, on reste déconnecté localement
      }
    } catch (error) {
      logger.error('Exception lors de la déconnexion:', error);
      // En cas d'exception, s'assurer que l'état local est nettoyé
      setUser(null);
      setSupabaseUser(null);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      return { error };
    } catch (error) {
      if (isAbortError(error)) {
        return { error: null };
      }
      throw error;
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      return { error };
    } catch (error) {
      if (isAbortError(error)) {
        return { error: null };
      }
      throw error;
    }
  };

  const updateProfile = async (data: Partial<Profile>) => {
    if (!user) return { error: 'Non connecté' };

    const { error } = await supabase
      .from('profiles')
      .update(data)
      .eq('id', user.id);

    if (error) {
      return { error: error.message };
    }

    await refreshUser();
    return { error: null };
  };

  const deleteAccount = async () => {
    if (!user) return { error: 'Non connecté' };

    try {
      // Supprimer le profil de la base de données
      const { error: deleteError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', user.id);

      if (deleteError) {
        return { error: deleteError.message };
      }

      // Déconnecter l'utilisateur
      await signOut();

      return { error: null };
    } catch (err) {
      logger.error('Erreur suppression compte:', err);
      return { error: err instanceof Error ? err.message : 'Erreur lors de la suppression' };
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
    deleteAccount,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
}
